import React, { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import '../styles/CurrencyAdEditModal.css';

const defaultVolumeDiscount = () => [{ min_quantity: 0, discount_percent: 0 }];

const ALBION_SERVER_OPTIONS = ['Albion Europe', 'Albion Americas', 'Albion Asia'];

const isAlbionCurrencyAd = (ad) => {
  if (!ad) return false;
  const g = (ad.game || '').toLowerCase();
  const st = (ad.service_type || '').toLowerCase();
  return (g.includes('albion') && g.includes('online')) && st === 'currency';
};

/**
 * Edit modal for currency ads: description, stock, min purchase, volume discounts.
 * For Albion Online currency: also Server (region) so seller can switch Europe / Americas / Asia.
 * Used from Seller Dashboard and OffersTab when editing a currency ad.
 */
const CurrencyAdEditModal = ({ ad, onSave, onCancel }) => {
  const rawMeta = ad?.metadata;
  const meta = rawMeta && typeof rawMeta === 'object' ? rawMeta : (typeof rawMeta === 'string' ? (() => { try { return JSON.parse(rawMeta); } catch { return {}; } })() : {});
  const [description, setDescription] = useState(ad?.description ?? '');
  const [quantity, setQuantity] = useState(ad?.quantity != null ? Number(ad.quantity) : 0);
  const [minQuantity, setMinQuantity] = useState(meta.min_quantity != null ? Number(meta.min_quantity) : 1);
  const [platform, setPlatform] = useState(() => {
    const p = (ad?.platform || '').trim();
    if (ALBION_SERVER_OPTIONS.some((s) => s.toLowerCase() === p.toLowerCase())) return p;
    if (p.toLowerCase().includes('europe')) return 'Albion Europe';
    if (p.toLowerCase().includes('americas')) return 'Albion Americas';
    if (p.toLowerCase().includes('asia')) return 'Albion Asia';
    return 'Albion Europe';
  });
  const [volumeDiscount, setVolumeDiscount] = useState(
    meta.volume_discount && Array.isArray(meta.volume_discount) && meta.volume_discount.length > 0
      ? meta.volume_discount.map((r) => ({ min_quantity: Number(r.min_quantity) || 0, discount_percent: Number(r.discount_percent) || 0 }))
      : defaultVolumeDiscount()
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDescription(ad?.description ?? '');
    setQuantity(ad?.quantity != null ? Number(ad.quantity) : 0);
    const m = ad?.metadata && typeof ad.metadata === 'object' ? ad.metadata : (typeof ad?.metadata === 'string' ? (() => { try { return JSON.parse(ad.metadata); } catch { return {}; } })() : {});
    setMinQuantity(m.min_quantity != null ? Number(m.min_quantity) : 1);
    const p = (ad?.platform || '').trim();
    if (ALBION_SERVER_OPTIONS.some((s) => s.toLowerCase() === p.toLowerCase())) setPlatform(p);
    else if (p.toLowerCase().includes('europe')) setPlatform('Albion Europe');
    else if (p.toLowerCase().includes('americas')) setPlatform('Albion Americas');
    else if (p.toLowerCase().includes('asia')) setPlatform('Albion Asia');
    else if (isAlbionCurrencyAd(ad)) setPlatform('Albion Europe');
    setVolumeDiscount(
      m.volume_discount && Array.isArray(m.volume_discount) && m.volume_discount.length > 0
        ? m.volume_discount.map((r) => ({ min_quantity: Number(r.min_quantity) || 0, discount_percent: Number(r.discount_percent) || 0 }))
        : defaultVolumeDiscount()
    );
  }, [ad]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (saving) return;
    const qty = Math.max(0, Number(quantity) || 0);
    const minQ = Math.max(1, Number(minQuantity) || 1);
    const tiers = (volumeDiscount || defaultVolumeDiscount()).map((r) => ({
      min_quantity: Math.max(0, Number(r.min_quantity) || 0),
      discount_percent: Math.min(100, Math.max(0, Number(r.discount_percent) || 0)),
    }));
    setSaving(true);
    const payload = {
      description: description,
      quantity: qty,
      metadata: { ...meta, min_quantity: minQ, volume_discount: tiers },
    };
    if (isAlbionCurrencyAd(ad)) payload.platform = (platform || 'Albion Europe').trim();
    Promise.resolve(onSave(payload)).finally(() => setSaving(false));
  };

  if (!ad) return null;

  const unitLabel = meta.unit_label || (/8\s*ball\s*pool|8-ball-pool/i.test(ad.game || '') ? 'B' : (/albion\s*online|animal\s*crossing|animal-crossing-new-horizons/i.test(ad.game || '') ? 'M' : (/arc\s*raiders/i.test(ad.game || '') ? 'K' : '')));
  const tiers = volumeDiscount && volumeDiscount.length > 0 ? volumeDiscount : defaultVolumeDiscount();

  return (
    <div className="currency-edit-modal-overlay" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="currency-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="currency-edit-modal-header">
          <h2 className="currency-edit-modal-title">Edit currency ad</h2>
          <button type="button" className="currency-edit-modal-close" onClick={onCancel} aria-label="Close">×</button>
        </div>
        <form onSubmit={handleSubmit} className="currency-edit-modal-form">
          {isAlbionCurrencyAd(ad) && (
            <div className="currency-edit-form-group">
              <label>Server (region)</label>
              <div className="currency-edit-server-buttons">
                {ALBION_SERVER_OPTIONS.map((server) => (
                  <button
                    key={server}
                    type="button"
                    className={`currency-edit-server-btn ${platform === server ? 'selected' : ''}`}
                    onClick={() => setPlatform(server)}
                  >
                    {server.replace('Albion ', '')}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="currency-edit-form-group">
            <label>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Describe your offer..."
              className="currency-edit-input"
            />
          </div>
          <div className="currency-edit-form-group">
            <label>Stock ({unitLabel || 'units'})</label>
            <input
              type="number"
              min={0}
              value={quantity === '' || Number.isNaN(quantity) ? '' : quantity}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '') setQuantity('');
                else {
                  const n = parseInt(v, 10);
                  if (!Number.isNaN(n)) setQuantity(Math.max(0, n));
                }
              }}
              className="currency-edit-input"
            />
            <span className="currency-edit-hint">Total quantity available. Set 0 for sold out.</span>
          </div>
          <div className="currency-edit-form-group">
            <label>Min. purchase ({unitLabel || 'units'})</label>
            <input
              type="number"
              min={1}
              value={minQuantity}
              onChange={(e) => setMinQuantity(e.target.value === '' ? 1 : Math.max(1, parseInt(e.target.value, 10) || 1))}
              className="currency-edit-input"
            />
          </div>

          <div className="currency-edit-volume-discount">
            <h3 className="currency-edit-volume-title">Volume discount</h3>
            <div className="currency-edit-volume-headers">
              <span>Min. quantity ({unitLabel || 'units'})</span>
              <span>Discount %</span>
              <span className="currency-edit-volume-actions" />
            </div>
            {tiers.map((row, index) => (
              <div key={index} className="currency-edit-volume-row">
                <div className="currency-edit-volume-cell">
                  <input
                    type="number"
                    min={0}
                    value={row.min_quantity ?? ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? '' : parseInt(e.target.value, 10);
                      const next = [...tiers];
                      next[index] = { ...next[index], min_quantity: val === '' ? 0 : (Number.isNaN(val) ? next[index].min_quantity : Math.max(0, val)) };
                      setVolumeDiscount(next);
                    }}
                    placeholder="0"
                    className="currency-edit-input currency-edit-volume-input"
                  />
                  {unitLabel ? <span className="currency-edit-volume-unit">{unitLabel}</span> : null}
                </div>
                <div className="currency-edit-volume-cell">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={row.discount_percent ?? ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? '' : parseFloat(e.target.value);
                      const next = [...tiers];
                      next[index] = { ...next[index], discount_percent: val === '' ? 0 : (Number.isNaN(val) ? next[index].discount_percent : Math.min(100, Math.max(0, val))) };
                      setVolumeDiscount(next);
                    }}
                    placeholder="0"
                    className="currency-edit-input currency-edit-volume-input"
                  />
                  <span className="currency-edit-volume-unit">%</span>
                </div>
                <div className="currency-edit-volume-actions">
                  <button
                    type="button"
                    onClick={() => {
                      const next = tiers.filter((_, i) => i !== index);
                      if (next.length === 0) next.push({ min_quantity: 0, discount_percent: 0 });
                      setVolumeDiscount(next);
                    }}
                    className="currency-edit-volume-delete"
                    aria-label="Remove tier"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setVolumeDiscount([...tiers, { min_quantity: 0, discount_percent: 0 }])}
              className="currency-edit-volume-add"
            >
              + Add tier
            </button>
          </div>

          <div className="currency-edit-modal-actions">
            <button type="button" className="currency-edit-btn-secondary" onClick={onCancel}>Cancel</button>
            <button type="submit" className="currency-edit-btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CurrencyAdEditModal;
